export const MEDIA_EXTS = new Set(["mp3", "wav", "m4a", "mp4", "webm", "mov"])

const MIME_MAP: Record<string, string> = {
  pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ppt: "application/vnd.ms-powerpoint",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  csv: "text/csv",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime"
}

/**
 * Derive MIME type from a filename's extension.
 * Falls back to "application/octet-stream" for unknown extensions.
 */
export function getMimeFromName(filename: string): string {
  const ext = (filename?.split(".").pop() ?? "").toLowerCase()
  return MIME_MAP[ext] ?? "application/octet-stream"
}

/**
 * Collapse the rfp_user_scoped_files composite id `"${tenant}|${user}|${file_id}"`
 * to its bare file_id (the base64 of the s3 path). Picker entries arrive from
 * different sources with the same logical file expressed in different id
 * shapes — the My Files list returns `file_id` as the bare base64, while the
 * notify-uploaded endpoint returns only the composite `id`. Without
 * normalization the picker's dedup (string-equal on `file_id`) treats them as
 * distinct entries and the attach POST ships both shapes of the same file,
 * producing the "added 2 files but 3 got added" symptom on dev (see Slack
 * 1779771769.259519 — pptx appeared with both `9|113|<base64>` and the bare
 * base64 in the same payload).
 *
 * Pass-through for ids that don't carry the `|tenant|user|` prefix (KB chunk
 * ids, shared file ids that already arrive in bare form).
 */
export function normalizeFileId(id: string | null | undefined): string {
  if (!id) return ""
  return id.includes("|") ? (id.split("|").pop() ?? id) : id
}

export type FileSource = "my_files" | "shared" | "kb"

export interface ISelectedFile {
  source: FileSource
  file_id: string
  s3_path: string
  file_name: string
  mime_type: string
  is_media: boolean
  transcription_status?: "pending" | "ready" | "failed"
}

export interface SelectFilesPickerProps {
  open: boolean
  onClose: () => void
  onSelect: (files: ISelectedFile[]) => void
  maxFiles: number
  maxFileSizeMB?: number
  accept: string
  availableTabs: FileSource[]
  currentlyAttached?: string[]
  /** Required for "shared" tab — RFP-OS session id */
  rfpSessionId?: string
  /** Modal heading. Defaults to "Select Files". The fill-template flow
   *  passes "Select a file to fill". */
  title?: string
  /** Confirm-button label. Defaults to "Attach (N)". The fill-template flow
   *  passes a "✓ Fill template" node. */
  confirmLabel?: import("react").ReactNode
  /** When true, show a "TEMPLATE" tag on the selected row(s) — used by the
   *  fill-template flow so the chosen file reads as the template to fill. */
  showTemplateTag?: boolean
  /** Lowercase extensions (no dot) that may actually be SELECTED, e.g.
   *  ["xlsx","docx"]. When set, all files still render but rows of other
   *  types are disabled, and the upload pane is restricted to these. */
  selectableExtensions?: string[]
}
